import { useState } from 'react';
import {
  UserPlus,
  MoreHorizontal,
  Shield,
  Users as UsersIcon,
  Check,
  X,
  Mail,
  Clock,
  RefreshCw,
  Trash2,
  Settings,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toaster';
import { normalizeError } from '@/lib/api';
import { cn, getInitials } from '@/lib/utils';
import { useUsers, useUpdateUserRole, useDeactivateUser, useActivateUser } from '@/hooks/use-users';
import { useInvites, useRegenerateInvite, useDeleteInvite } from '@/hooks/use-invites';
import { useAuth } from '@/hooks/use-auth';
import { usePermissions } from '@/hooks/use-permissions';
import { InviteUserDialog } from '@/features/users/components/InviteUserDialog';
import { UserManagementModal } from '@/features/users/components/UserManagementModal';
import type { GlobalRole } from '@/types';
import { formatDistanceToNow } from 'date-fns';

const ROLE_LABELS: Record<GlobalRole, string> = {
  admin: 'Admin',
  supervisor: 'Supervisor',
  user: 'User',
  client: 'Client',
};

const ROLE_COLORS: Record<GlobalRole, string> = {
  admin: 'bg-primary text-primary-foreground',
  supervisor: 'bg-purple-500/20 text-purple-500',
  user: 'bg-blue-500/20 text-blue-500',
  client: 'bg-orange-500/20 text-orange-500',
};

export function UsersPage() {
  const { user: currentUser } = useAuth();
  const { hasCapability } = usePermissions();
  const { data: users = [], isLoading: usersLoading } = useUsers();
  const { data: invites = [], isLoading: invitesLoading } = useInvites();
  const updateRole = useUpdateUserRole();
  const deactivateUser = useDeactivateUser();
  const activateUser = useActivateUser();
  const regenerateInvite = useRegenerateInvite();
  const deleteInvite = useDeleteInvite();

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editingRoleUserId, setEditingRoleUserId] = useState<string | null>(null);
  const [managingUserId, setManagingUserId] = useState<string | null>(null);

  const canManageUsers = hasCapability('user.edit');

  const handleRoleChange = async (userId: string, newRole: GlobalRole) => {
    try {
      await updateRole.mutateAsync({ id: userId, role: newRole });
      toast({ title: 'Role updated successfully' });
      setEditingRoleUserId(null);
    } catch (error: unknown) {
      toast({
        title: 'Failed to update role',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    }
  };

  const handleDeactivate = async (userId: string) => {
    try {
      await deactivateUser.mutateAsync(userId);
      toast({ title: 'User deactivated' });
    } catch (error: unknown) {
      toast({
        title: 'Failed to deactivate user',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    }
  };

  const handleActivate = async (userId: string) => {
    try {
      await activateUser.mutateAsync(userId);
      toast({ title: 'User activated' });
    } catch (error: unknown) {
      toast({
        title: 'Failed to activate user',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    }
  };

  const handleRegenerateInvite = async (inviteId: string) => {
    try {
      const result = await regenerateInvite.mutateAsync(inviteId);
      await navigator.clipboard.writeText(result.inviteLink);
      toast({ title: 'Invite link regenerated and copied to clipboard' });
    } catch (error: unknown) {
      toast({
        title: 'Failed to regenerate invite',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    }
  };

  const handleDeleteInvite = async (inviteId: string) => {
    try {
      await deleteInvite.mutateAsync(inviteId);
      toast({ title: 'Invite deleted' });
    } catch (error: unknown) {
      toast({
        title: 'Failed to delete invite',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    }
  };

  const pendingInvites = invites.filter((i) => !i.acceptedAt && !i.isExpired);
  const expiredInvites = invites.filter((i) => i.isExpired);

  return (
    <div className="container max-w-5xl py-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Users</h1>
          <p className="text-muted-foreground">
            Manage users and invite new team members
          </p>
        </div>
        <Button onClick={() => setInviteDialogOpen(true)} className="gap-2">
          <UserPlus className="h-4 w-4" />
          Invite User
        </Button>
      </div>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UsersIcon className="h-5 w-5" />
            Team Members
          </CardTitle>
          <CardDescription>
            {users.length} user{users.length !== 1 ? 's' : ''} in your organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          {usersLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="space-y-1 pr-4">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className={cn(
                      'flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors',
                      !user.isActive && 'opacity-50',
                      canManageUsers && user.id !== currentUser?.id && 'cursor-pointer'
                    )}
                    onClick={() => {
                      if (canManageUsers && user.id !== currentUser?.id) {
                        setManagingUserId(user.id);
                      }
                    }}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar>
                        <AvatarImage src={user.avatarUrl || undefined} />
                        <AvatarFallback>{getInitials(user.displayName)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium truncate">{user.displayName}</p>
                          {user.id === currentUser?.id && (
                            <Badge variant="outline" className="text-xs">You</Badge>
                          )}
                          {!user.isActive && (
                            <Badge variant="secondary" className="text-xs">Inactive</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      {editingRoleUserId === user.id ? (
                        <div className="flex items-center gap-1">
                          <Select
                            defaultValue={user.role}
                            onValueChange={(value) => handleRoleChange(user.id, value as GlobalRole)}
                          >
                            <SelectTrigger className="w-28 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="supervisor">Supervisor</SelectItem>
                              <SelectItem value="user">User</SelectItem>
                              <SelectItem value="client">Client</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => setEditingRoleUserId(null)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <Badge className={cn('cursor-default', ROLE_COLORS[user.role])}>
                          {ROLE_LABELS[user.role]}
                        </Badge>
                      )}

                      {user.id !== currentUser?.id && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canManageUsers && (
                              <>
                                <DropdownMenuItem onClick={() => setManagingUserId(user.id)}>
                                  <Settings className="h-4 w-4 mr-2" />
                                  Manage user
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                              </>
                            )}
                            <DropdownMenuItem onClick={() => setEditingRoleUserId(user.id)}>
                              <Shield className="h-4 w-4 mr-2" />
                              Change role
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {user.isActive ? (
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => handleDeactivate(user.id)}
                              >
                                <X className="h-4 w-4 mr-2" />
                                Deactivate
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => handleActivate(user.id)}>
                                <Check className="h-4 w-4 mr-2" />
                                Activate
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Pending Invites */}
      {(pendingInvites.length > 0 || invitesLoading) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Pending Invites
            </CardTitle>
            <CardDescription>
              {pendingInvites.length} pending invite{pendingInvites.length !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {invitesLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : (
              <ScrollArea className="max-h-[300px]">
                <div className="space-y-2">
                  {pendingInvites.map((invite) => (
                    <div
                      key={invite.id}
                      className="flex items-center justify-between p-3 rounded-lg border"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                          <Mail className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{invite.email}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            <span>
                              Expires {formatDistanceToNow(new Date(invite.expiresAt), { addSuffix: true })}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Badge className={cn(ROLE_COLORS[invite.role])}>
                          {ROLE_LABELS[invite.role]}
                        </Badge>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleRegenerateInvite(invite.id)}>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Regenerate & Copy Link
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => handleDeleteInvite(invite.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      )}

      {/* Expired Invites (collapsed) */}
      {expiredInvites.length > 0 && (
        <Card className="opacity-75">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-muted-foreground">
              <Clock className="h-5 w-5" />
              Expired Invites
            </CardTitle>
            <CardDescription>
              {expiredInvites.length} expired invite{expiredInvites.length !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="max-h-[200px]">
              <div className="space-y-2">
                {expiredInvites.map((invite) => (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between p-2 rounded-lg text-sm text-muted-foreground"
                  >
                    <span>{invite.email}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {ROLE_LABELS[invite.role]}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => handleDeleteInvite(invite.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      <InviteUserDialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen} />
      
      <UserManagementModal
        userId={managingUserId}
        open={!!managingUserId}
        onOpenChange={(open) => {
          if (!open) setManagingUserId(null);
        }}
      />
    </div>
  );
}
